/**
 * El seguimiento de las facturas, como lo describe el contrato con el API
 * (`contrato-seguimiento.md`).
 *
 * A mano hasta que `pnpm api:types` traiga `list_invoice_reviews` y el
 * `get_invoice_review` ampliado. Los campos que el contrato AGREGA al detalle
 * van opcionales: el API los está sumando, y mientras tanto la pantalla los
 * saca de lo que ya llega (ver `detail.ts`).
 */
import type { SearchChip } from "../digitacion/api/contract.ts";
import type { StepKind } from "../review-flow/flow.ts";

export const RUN_STATUSES = [
  "running",
  "waiting",
  "completed",
  "failed",
  "superseded",
] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];

export type RunMode = "live" | "shadow";

/** Un resultado con su nombre: el tipo de archivo, el paso actual… */
export interface Named {
  readonly key: string;
  readonly label: string;
}

/* ------------------------------------------------------------------------ */
/* La lista: `list_invoice_reviews`                                          */
/* ------------------------------------------------------------------------ */

export interface InvoiceReviewFilters {
  readonly status?: RunStatus;
  /** El paso en el que está ahora. */
  readonly stepKey?: string;
  /** El resultado de «Identificar qué es la foto». */
  readonly photoType?: string;
  readonly snapId?: number;
  /** El buscador de un solo texto, el de Digitación. */
  readonly q?: string;
  /** La bolsa: el mes de emisión (`AAAA-MM`) o el año entero (`AAAA`). */
  readonly period?: string;
  /**
   * La decisión final; `none` es «sin decidir» y `decided`, cualquiera de las
   * tres (`contrato-progreso.md` §2).
   */
  readonly decision?: "valid" | "invalid" | "for_data" | "none" | "decided";
  /** Quién decidió: su id de auth. */
  readonly decidedBy?: string;
  /** Decididas desde (incluido) y hasta (sin incluir), en ISO. */
  readonly decidedFrom?: string;
  readonly decidedTo?: string;
  /** Por qué falló (`FAILURE_REASONS`): solo tiene sentido con las fallidas. */
  readonly failureReason?: string;
  /**
   * Si lala4 ya la decidió. `false` es lo que es trabajo de este sistema:
   * «Con problema» y «Reprocesar todas» lo piden así.
   */
  readonly decidedInLala4?: boolean;
}

/** La decisión final de una fila; `null` si no se decidió. */
export interface InvoiceReviewDecision {
  readonly outcome: string;
  /** «Válida», «Inválida»… */
  readonly label: string;
  /** El nombre de quien decidió (o su id, si no se sabe). */
  readonly by: string;
  readonly at: string;
  /** El motivo y su comentario (nutriexpertos#38); `null` sin envío. Ausente en un API de antes. */
  readonly reason?: {
    readonly key: string;
    readonly comment: string | null;
  } | null;
}

export interface InvoiceReviewRow {
  readonly runId: string;
  readonly snapId: string;
  readonly flowVersion: number;
  readonly mode: RunMode;
  readonly status: RunStatus;
  readonly statusLabel: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** Cuándo la subió el dependiente. */
  readonly uploadedAt: string | null;
  readonly participantId: string | null;
  /** El dependiente que la subió: nombre y cédula. */
  readonly participantName?: string | null;
  readonly participantIdentifier?: string | null;
  readonly pharmacyName?: string | null;
  readonly chainName?: string | null;
  readonly resourceUrl: string;
  readonly currentStep: Named | null;
  readonly waitingSince: string | null;
  readonly fileKind: Named | null;
  readonly photoType: (Named & { readonly confidence: string | null }) | null;
  /** `null` si algún precio es desconocido: NO es cero. */
  readonly costUsd: number | null;
  /** Opcional: un API de antes de `contrato-progreso.md` no la manda. */
  readonly decision?: InvoiceReviewDecision | null;
  /**
   * Por qué falló, si el seguimiento está fallido. Ausente en un API de
   * antes de reprocesar desde la página: entonces no se ofrece.
   */
  readonly failure?: Failure | null;
  /**
   * Fallida aquí, pero lala4 ya la decidió: no es un problema por arreglar,
   * no se reprocesa. Ausente en un API de antes.
   */
  readonly decidedInLala4?: boolean;
  /**
   * Cuántas aprobadas espera esta factura para salir a lala4 (nutriexpertos#24,
   * en paralelo): mientras sea > 0, se ven «frenadas». Opcional: un API de
   * antes no lo manda, y entonces no se muestra nada.
   */
  readonly heldApprovals?: number;
}

/**
 * Por qué falló un seguimiento, en palabras de un digitador
 * (`reprocesar.md`). `reason` es una clasificación cerrada del error que
 * guardó el paso; las palabras las pone el back (`reasonLabel`).
 */
export const FAILURE_REASONS = [
  "model_limit",
  "model_unavailable",
  "timeout",
  "file",
  "invalid",
  "dispatch_lost",
  "other",
] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

export interface Failure {
  readonly stepKey: string;
  readonly stepLabel: string;
  /** Una de `FAILURE_REASONS`; una que el front no conozca también vale. */
  readonly reason: string;
  readonly reasonLabel: string;
  /** Cuándo falló; `null` si no se sabe. */
  readonly at: string | null;
}

/** Por qué falló UN intento: va en cada intento fallido del detalle. */
export interface FailureReasonView {
  readonly reason: string;
  readonly reasonLabel: string;
}

export interface InvoiceReviewPage {
  readonly items: readonly InvoiceReviewRow[];
  readonly nextCursor: string | null;
  /** Cómo entendió el API `q`: un chip por pedazo. */
  readonly interpretation?: readonly SearchChip[];
  /** Sobre TODO lo que cumple los filtros, no solo la página. */
  readonly counts: {
    readonly total: number;
    readonly byStatus: Readonly<Record<string, number>>;
    readonly byPhotoType: Readonly<Record<string, number>>;
  };
}

/* ------------------------------------------------------------------------ */
/* El detalle: `get_invoice_review`                                          */
/* ------------------------------------------------------------------------ */

export type AttemptStatus = "scheduled" | "waiting" | "done" | "failed";

/**
 * Dónde está un intento programado en la cola de su tipo de paso: espera su
 * turno, un job lo está ejecutando, o falló una vez y espera para reintentar.
 */
export type QueueState = "queued" | "running" | "retrying";

export interface AttemptQueue {
  readonly state: QueueState;
  /** «En cola», «Trabajando» o «Por reintentar». */
  readonly stateLabel: string;
  /** Cuántos se van a tomar antes que él; `null` si no espera turno. */
  readonly ahead: number | null;
}

/** El respaldo: lo que vio el paso y por qué respondió eso. */
export interface Evidence {
  readonly summary: string;
  readonly details?: Readonly<Record<string, unknown>>;
}

export interface InvoiceReviewAttempt {
  readonly attemptId: string;
  readonly stepKey: string;
  readonly stepVersion: number;
  readonly seq: number;
  readonly status: AttemptStatus;
  readonly outcome: string | null;
  readonly output: Readonly<Record<string, unknown>> | null;
  readonly evidence: Evidence | null;
  readonly error: Readonly<Record<string, unknown>> | null;
  readonly ticket: string | null;
  readonly inputHash: string | null;
  readonly tries: number;
  readonly reason: string;
  readonly requestedBy: string;
  readonly completedBy: string | null;
  readonly superseded: boolean;
  readonly supersededAt: string | null;
  readonly supersededByAttemptId: string | null;
  readonly createdAt: string;
  readonly waitingSince: string | null;
  readonly finishedAt: string | null;
  /* Lo que agrega el contrato. */
  readonly label?: string;
  readonly kind?: StepKind;
  readonly kindLabel?: string;
  readonly outcomeLabel?: string | null;
  /** `null` si no usó IA o el precio es desconocido. */
  readonly costUsd?: number | null;
  readonly durationMs?: number | null;
  /** Cuándo empezó a trabajar (la última ejecución); `null` si no empezó. */
  readonly startedAt?: string | null;
  /** Lo que esperó su turno en la cola; `null` si no se sabe. */
  readonly queuedMs?: number | null;
  /** Lo que trabajó; `null` si no terminó o no se sabe cuándo empezó. */
  readonly workedMs?: number | null;
  /** Si está programado, dónde está en su cola ahora. */
  readonly queue?: AttemptQueue | null;
  /** Si falló: por qué, en palabras de un digitador. */
  readonly failureReason?: FailureReasonView | null;
}

export interface InvoiceReview {
  readonly runId: string;
  readonly snapId: string;
  readonly flowId: string;
  readonly flowVersion: number;
  readonly mode: string;
  readonly status: RunStatus;
  readonly origin: string;
  readonly previousRunId: string | null;
  readonly startedBy: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** En el orden en que se programaron, reemplazados incluidos. */
  readonly attempts: readonly InvoiceReviewAttempt[];
  /** La ficha de la factura: lo que el seguimiento ya sabe de ella. */
  readonly dossier: Readonly<Record<string, unknown>>;
  /* Lo que agrega el contrato. */
  readonly statusLabel?: string;
  readonly resourceUrl?: string;
  readonly uploadedAt?: string | null;
  readonly costUsd?: number | null;
  /** Los pasos por los que pasó, en orden y sin los reemplazados. */
  readonly path?: readonly string[];
  /**
   * Las líneas de la factura, como valen AHORA: un alias confirmado después
   * las asigna sin que el seguimiento vuelva a correr. Vacío si la factura
   * aún no se leyó; ausente en un API de antes de «Reconocer productos».
   */
  readonly lines?: readonly InvoiceLine[];
  /** Si el seguimiento está fallido: por qué y en qué paso. */
  readonly failure?: Failure | null;
  /**
   * Si lala4 ya la decidió: la cerró lala4, o falló aquí y lala4 ya la
   * decidió. Con `true` no se reprocesa. Ausente en un API de antes.
   */
  readonly decidedInLala4?: boolean;
  /**
   * El giro VIGENTE (`effectiveRotationOf`, §18 del contrato de digitación):
   * el de «Enderezar la foto», o el corregido a mano si lo hay. Ausente en
   * un API de antes: se sigue sacando de `dossier` (`rotationOf`).
   */
  readonly rotation?: 0 | 90 | 180 | 270;
  /**
   * La corrección a mano del giro, si la hay: lo que dijo la IA, lo
   * corregido, quién y cuándo. `null` sin corregir; ausente en un API de
   * antes de nutriexpertos#13.
   */
  readonly rotationCorrection?: {
    readonly aiRotation: 0 | 90 | 180 | 270;
    readonly correctedRotation: 0 | 90 | 180 | 270;
    /** El id de auth: no se enseña, se usa `correctedByName`. */
    readonly correctedBy: string;
    readonly correctedByName: string;
    readonly correctedAt: string;
  } | null;
}

/* ------------------------------------------------------------------------ */
/* Las líneas de la factura: lo que deja «Reconocer productos»               */
/* ------------------------------------------------------------------------ */

/** Las claves: deciden el color, nunca el texto (para eso, `*Label`). */
export type LineAssignment = "product" | "not_product" | "unknown";
export type LineAssigner = "step" | "alias_confirmed" | "reviewer";
export type PriceSignal = "ok" | "low" | "high" | "multiple" | "unknown";

/** La señal de precio: un indicador, nunca decide. */
export interface LinePrice {
  /** El P.V.P. leído. */
  readonly unitPrice: number | null;
  /** El precio de referencia del producto. */
  readonly reference: {
    /** El precio más común (mediana). */
    readonly median: number;
    readonly p10: number;
    readonly p90: number;
    readonly scope: "chain" | "global";
    readonly period: string;
  } | null;
  /** P.V.P. leído / precio más común; `null` si falta alguno. */
  readonly ratio: number | null;
  readonly signal: PriceSignal;
  readonly multipleOf?: number;
  /** «En rango», «Bajo», «Alto», «Múltiplo (×60)», «Sin referencia». */
  readonly signalLabel: string;
}

export interface InvoiceLine {
  /** El índice en `invoice.read.items`. */
  readonly lineIndex: number;
  /** Como se leyó. */
  readonly rawDescription: string;
  readonly normalizedText: string;
  readonly printedQuantity: number | null;
  /** El P.V.P. leído. */
  readonly unitPrice: number | null;
  readonly assignment: LineAssignment;
  /** «Producto», «No es producto», «Sin reconocer». */
  readonly assignmentLabel: string;
  /** Id de lala4 del producto (nivel 4), si es producto. */
  readonly productId: number | null;
  /**
   * El producto con su nombre del catálogo; `null` si no es un producto.
   * Ausente en un API de antes de que lo mandara: entonces queda el id.
   */
  readonly product?: { readonly id: number; readonly name: string } | null;
  readonly aliasId: string | null;
  /** «Unidades por línea». */
  readonly unitsPerLine: number;
  readonly unitsPerLineSource: string;
  /** «Sin alias», «Del alias». */
  readonly unitsPerLineSourceLabel: string;
  readonly assignedBy: LineAssigner;
  /** «Reconocer productos», «Asignado por alias nuevo», «Digitador». */
  readonly assignedByLabel: string;
  readonly assignedAt: string;
  /** `null` si la línea no era producto cuando corrió el paso. */
  readonly price: LinePrice | null;
}
